import { createContext, useContext, useState, ReactNode } from "react";

interface ComparisonActivity {
  id: string;
  title: string;
  slug: string;
  image: string;
  price: number;
  location: string;
  duration: string;
  rating: number;
}

interface ComparisonContextType {
  comparedActivities: ComparisonActivity[];
  addToComparison: (activity: ComparisonActivity) => void;
  removeFromComparison: (id: string) => void;
  clearComparison: () => void;
  isInComparison: (id: string) => boolean;
  canAddMore: boolean;
}

const ComparisonContext = createContext<ComparisonContextType | undefined>(undefined);

const MAX_COMPARISON = 3;

export function ComparisonProvider({ children }: { children: ReactNode }) {
  const [comparedActivities, setComparedActivities] = useState<ComparisonActivity[]>([]);

  const addToComparison = (activity: ComparisonActivity) => {
    if (comparedActivities.length >= MAX_COMPARISON) return;
    if (comparedActivities.some((a) => a.id === activity.id)) return;
    setComparedActivities((prev) => [...prev, activity]);
  };

  const removeFromComparison = (id: string) => {
    setComparedActivities((prev) => prev.filter((a) => a.id !== id));
  };

  const clearComparison = () => {
    setComparedActivities([]);
  };

  const isInComparison = (id: string) => {
    return comparedActivities.some((a) => a.id === id);
  };

  const canAddMore = comparedActivities.length < MAX_COMPARISON;

  return (
    <ComparisonContext.Provider
      value={{
        comparedActivities,
        addToComparison,
        removeFromComparison,
        clearComparison,
        isInComparison,
        canAddMore,
      }}
    >
      {children}
    </ComparisonContext.Provider>
  );
}

export function useComparison() {
  const context = useContext(ComparisonContext);
  if (context === undefined) {
    throw new Error("useComparison must be used within a ComparisonProvider");
  }
  return context;
}
